import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect, useState, useContext, useRef } from 'react';
import Loader from '../shared/Loader';
import { useDispatch } from 'react-redux';
import { addToCart } from '../../store/cartSlice';
import { ProductContext } from '../../providers/ProductContext';
import { FormattedDescription, HtmlFormattedDescription } from '../../utils/formatDescription';
import { showError, showCartToast } from '../../utils/sweetAlert';
import TryOnModal from './TryOnModal';
import CustomOrderForm from './CustomOrderForm';
import SizeGuideModal from './SizeGuideModal';
import ProductCard from './ProductCard';
import SEO from '../shared/SEO';
import { getDisplayImageUrl } from '../../utils/imageUtils';
import { CATEGORY_LANDINGS, SITE_NAME } from '../../config/siteContent';
import { trackViewContent } from '../../utils/metaPixel';


const SingleProduct = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { products } = useContext(ProductContext);
  
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [selectedColors, setSelectedColors] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showTryOnModal, setShowTryOnModal] = useState(false);
  const [showCustomOrderForm, setShowCustomOrderForm] = useState(false);
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [selectedAIProductType, setSelectedAIProductType] = useState('');
  const [selectedAISize, setSelectedAISize] = useState('');
  const [selectedAIColor, setSelectedAIColor] = useState('');
  const viewedProductId = useRef('');

  // Tipos de producto disponibles para productos AI con sus precios
  // Los precios se obtienen de las variantes del producto (MongoDB)
  // Índice 0 = Remera, Índice 1 = Cuadro, Índice 2 = Almohadón
  const getAIProductTypes = () => {
    if (!product?.variants || product.variants.length < 3) {
      // Fallback si no hay variantes
      return [
        { id: 'cuadro', name: 'Cuadro', price: 0, icon: 'fa-solid fa-image' },
        { id: 'remera', name: 'Remera', price: 0, icon: 'fa-solid fa-shirt' },
        { id: 'almohadon', name: 'Almohadón', price: 0, icon: 'fa-solid fa-couch' }
      ];
    }
    return [
      { id: 'cuadro', name: 'Cuadro', price: product.variants[1]?.price || 0, icon: 'fa-solid fa-image' },
      { id: 'remera', name: 'Remera', price: product.variants[0]?.price || 0, icon: 'fa-solid fa-shirt' },
      { id: 'almohadon', name: 'Almohadón', price: product.variants[2]?.price || 0, icon: 'fa-solid fa-couch' }
    ];
  };

  // Talles y colores disponibles para remeras AI
  const aiRemeraSizes = ['S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
  const aiRemeraColors = ['Blanco', 'Negro', 'Gris'];

  useEffect(() => {
    // Scroll to top cuando se carga un nuevo producto
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Buscar primero en el contexto (evita fetch redundante)
    const cached = products.find(p => p._id === id);
    if (cached) {
      setProduct(cached);
      setLoading(false);
      return;
    }

    // Fallback: fetch individual si no está en contexto (acceso directo por URL)
    fetch(`${process.env.REACT_APP_API_URL}/products/${id}`)
      .then(res => res.json())
      .then(data => {
        setProduct(data);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setError('Error al cargar el producto');
        setLoading(false);
      });
  }, [id, products]);

  useEffect(() => {
    if (!product?._id || viewedProductId.current === product._id) return;
    viewedProductId.current = product._id;
    trackViewContent(product);
  }, [product]);

  // Obtener variantes disponibles y opciones únicas
  const getAvailableSizes = () => {
    if (!product?.hasVariants || !product.variants?.length) {
      return product?.size || [];
    }
    return [...new Set(product.variants.filter(v => v.isActive !== false).map(v => v.size))];
  };

  const getAvailableColors = () => {
    if (!product?.hasVariants || !product.variants?.length) {
      return product?.color || [];
    }
    return [...new Set(product.variants.filter(v => v.isActive !== false).map(v => v.color))];
  };

  const getColorHex = (colorName) => (
    product?.colorOptions?.find((color) => color.name?.toLowerCase() === colorName?.toLowerCase?.())?.hex || null
  );

  // Maneja la selección de talla
  const handleSizeChange = (size) => {
    setSelectedSize(size);
    updateSelectedVariant(size, selectedColor);
  };

  // Maneja la selección de color
  const handleColorChange = (color) => {
    setSelectedColor(color);
    updateSelectedVariant(selectedSize, color);
  };

  // Actualiza la variante seleccionada
  const updateSelectedVariant = (size, color) => {
    if (!product?.hasVariants || !size || !color) {
      setSelectedVariant(null);
      return;
    }

    const variant = product.variants.find(v => 
      v.size === size && v.color === color && v.isActive !== false
    );
    setSelectedVariant(variant || null);
  };

  // Maneja la selección múltiple para talles (legacy)
  const handleSizeSelectChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
    setSelectedSizes(selectedOptions);
  };

  // Maneja la selección múltiple para colores (legacy)
  const handleColorSelectChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
    setSelectedColors(selectedOptions);
  };

  const handleAddToCart = () => {
    // Validación para productos AI
    if (isAIProduct()) {
      if (!selectedAIProductType) {
        showError('Por favor, selecciona el tipo de producto (Cuadro, Remera o Almohadón)');
        return;
      }
      
      // Validación adicional para remeras AI
      if (selectedAIProductType === 'remera') {
        if (!selectedAISize) {
          showError('Por favor, selecciona una talla para la remera');
          return;
        }
        if (!selectedAIColor) {
          showError('Por favor, selecciona un color para la remera');
          return;
        }
      }
      
      const selectedType = getAIProductTypes().find(t => t.id === selectedAIProductType);
      const finalPrice = product.discount > 0 
        ? selectedType.price - (selectedType.price * product.discount / 100)
        : selectedType.price;

      // Construir info de variante según el tipo
      const variantInfo = selectedAIProductType === 'remera' 
        ? `${selectedType.name} - ${selectedAISize} - ${selectedAIColor}`
        : selectedType.name;

      dispatch(addToCart({
        ...product,
        price: finalPrice,
        quantity,
        selectedVariant: {
          size: selectedAIProductType === 'remera' ? selectedAISize : selectedType.name,
          color: selectedAIProductType === 'remera' ? selectedAIColor : 'Único',
          productType: selectedAIProductType
        },
        variantInfo,
        isAIProduct: true
      }));
      
      showCartToast(`${product.name} - ${variantInfo}`);
      return;
    }

    if (product.hasVariants && product.variants?.length > 0) {
      // Validación para productos con variantes
      if (!selectedSize) {
        showError('Por favor, selecciona una talla');
        return;
      }
      if (!selectedColor) {
        showError('Por favor, selecciona un color');
        return;
      }
      if (!selectedVariant) {
        showError('La combinación seleccionada no está disponible');
        return;
      }

      // Calcular precio de la variante
      const variantPrice = selectedVariant.price || product.basePrice || product.price;
      const finalPrice = product.discount > 0 
        ? variantPrice - (variantPrice * product.discount / 100)
        : variantPrice;

      dispatch(addToCart({
        ...product,
        price: finalPrice,
        quantity,
        selectedVariant: {
          size: selectedVariant.size,
          color: selectedVariant.color,
          sku: selectedVariant.sku,
          variantId: selectedVariant._id,
          images: selectedVariant.images || []
        },
        variantInfo: `${selectedVariant.size} - ${selectedVariant.color}`
      }));
      
      showCartToast(product.name);
    } else {
      // Validaciones legacy para productos sin variantes
      if (product.size?.length && selectedSizes.length === 0) {
        showError('Por favor, selecciona al menos un talle');
        return;
      }
      if (product.color?.length && selectedColors.length === 0) {
        showError('Por favor, selecciona al menos un color');
        return;
      }
      
      // Calcular el precio final para productos legacy
      const basePrice = product.basePrice || product.price;
      const finalPrice = product.discount > 0 
        ? basePrice - (basePrice * product.discount / 100)
        : basePrice;
    
      dispatch(addToCart({
        ...product,
        price: finalPrice,
        quantity,
        selectedSizes,
        selectedColors
      }));
      
      showCartToast(product.name);
    }
  };
  

  if (loading) {
    return <Loader />;
  }
  
  if (error) {
    return (
      <div className="single-product-error">
        <div className="container">
          <div className="error-content">
            <i className="fa-solid fa-exclamation-triangle"></i>
            <h3>Error al cargar el producto</h3>
            <p>{error}</p>
            <button 
              className="btn-retry"
              onClick={() => window.location.reload()}
            >
              <i className="fa-solid fa-refresh me-2"></i>
              Intentar de nuevo
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  if (!product) {
    return (
      <div className="single-product-not-found">
        <div className="container">
          <div className="not-found-content">
            <i className="fa-solid fa-search"></i>
            <h3>Producto no encontrado</h3>
            <p>El producto que buscas no existe o ha sido eliminado</p>
            <button 
              className="btn-back-store"
              onClick={() => window.history.back()}
            >
              <i className="fa-solid fa-arrow-left me-2"></i>
              Volver a la tienda
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Obtener todas las imágenes disponibles
  const getAllImages = () => {
    // Priorizar imágenes del producto principal
    if (product.images?.length > 0) {
      return product.images.map(img => getDisplayImageUrl(img));
    }
    
    // Si no hay array de imágenes, usar la imagen principal
    if (product.image) {
      const imageUrl = getDisplayImageUrl(product.image);
      return [imageUrl];
    }
    
    // Fallback a imagen por defecto
    return ['https://via.placeholder.com/400x400?text=Sin+Imagen'];
  };

  const allImages = getAllImages();
  const currentImage = allImages[currentImageIndex] || allImages[0];

  // Funciones para navegación de imágenes
  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  };

  const goToImage = (index) => {
    setCurrentImageIndex(index);
  };

  // Check if product is AI category
  const isAIProduct = () => {
    if (!product) return false;
    const categoryName = typeof product.category === 'object' 
      ? product.category?.name 
      : product.category;
    return categoryName === 'AI';
  };

  // Calcular precio actual (variante o base)
  const getCurrentPrice = () => {
    // Para productos AI, usar el precio del tipo seleccionado
    if (isAIProduct() && selectedAIProductType) {
      const selectedType = getAIProductTypes().find(t => t.id === selectedAIProductType);
      return selectedType?.price || product.basePrice || product.price;
    }
    
    if (selectedVariant) {
      return selectedVariant.price || product.basePrice || product.price;
    }
    return product.basePrice || product.price;
  };

  const currentPrice = getCurrentPrice();
  
  // Calcula el precio con descuento
  const discountedPrice = product.discount > 0 
    ? (currentPrice - currentPrice * product.discount / 100).toFixed(2)
    : currentPrice;

  const ahorro = (currentPrice - discountedPrice).toFixed(2);

  // Check if product is suitable for virtual try-on (clothing items)
  const isTryOnEligible = () => {
    if (!product) return false;
    
    const categoryName = typeof product.category === 'object' 
      ? product.category?.name?.toLowerCase() 
      : product.category?.toLowerCase();
    
    const productName = product.name?.toLowerCase() || '';
    const subcategory = product.subcategory?.toLowerCase() || '';
    
    // Check for clothing categories and keywords
    const clothingKeywords = [
      'remera', 'camiseta', 'playera', 'polo', 'camisa', 'blusa',
      'sweater', 'hoodie', 'sudadera', 'jersey', 'cardigan',
      'vestido', 'falda', 'pantalon', 'jean', 'short',
      'chaqueta', 'campera', 'abrigo', 'saco'
    ];
    
    const clothingCategories = [
      'ropa', 'clothing', 'apparel', 'fashion', 'moda',
      'remeras', 'camisetas', 'tops', 'bottoms'
    ];
    
    return clothingCategories.some(cat => categoryName?.includes(cat)) ||
           clothingKeywords.some(keyword => 
             productName.includes(keyword) || 
             subcategory.includes(keyword)
           );
  };

  // Check if product is customizable (Personalizados category)
  const isCustomizable = () => {
    if (!product) return false;
    
    const categoryName = typeof product.category === 'object' 
      ? product.category?.name?.toLowerCase() 
      : product.category?.toLowerCase();
    
    const productName = product.name?.toLowerCase() || '';
    const subcategory = product.subcategory?.toLowerCase() || '';
    
    const customKeywords = [
      'personalizado', 'personalizados', 'custom', 'estampado',
      'sublimacion', 'sublimación', 'vinilo', 'dtf'
    ];
    
    const customCategories = [
      'personalizados', 'personalizado', 'custom', 'estampados'
    ];
    
    return customCategories.some(cat => categoryName?.includes(cat)) ||
           customKeywords.some(keyword => 
             productName.includes(keyword) || 
             subcategory.includes(keyword) ||
             categoryName?.includes(keyword)
           );
  };

  // Get product type for size guide (remera, buzo, canguro)
  const getSizeGuideType = () => {
    if (!product) return null;
    
    const productName = product.name?.toLowerCase() || '';
    const categoryName = typeof product.category === 'object' 
      ? product.category?.name?.toLowerCase() 
      : product.category?.toLowerCase() || '';
    const subcategory = product.subcategory?.toLowerCase() || '';
    
    const searchText = `${productName} ${categoryName} ${subcategory}`;
    
    // Detectar remeras
    if (searchText.includes('remera') || searchText.includes('camiseta') || 
        searchText.includes('t-shirt') || searchText.includes('tshirt') ||
        searchText.includes('polera')) {
      return 'remera';
    }
    
    // Detectar buzos (para futuro)
    if (searchText.includes('buzo') || searchText.includes('sudadera') || 
        searchText.includes('sweater') || searchText.includes('suéter')) {
      return 'buzo';
    }
    
    // Detectar canguros (para futuro)
    if (searchText.includes('canguro') || searchText.includes('hoodie') || 
        searchText.includes('capucha')) {
      return 'canguro';
    }
    
    return null;
  };

  const hasCustomSizeGuide = Array.isArray(product?.sizeGuide?.columns) &&
    product.sizeGuide.columns.length > 0 &&
    Array.isArray(product?.sizeGuide?.rows) &&
    product.sizeGuide.rows.length > 0;

  const fallbackSizeGuideType = getSizeGuideType();
  const canShowSizeGuide = hasCustomSizeGuide || Boolean(fallbackSizeGuideType);
  const sizeGuideButtonLabel = hasCustomSizeGuide ? 'Ver tabla de medidas' : 'Ver guía de talles';
  const shouldShowStandaloneSizeGuide = hasCustomSizeGuide &&
    !isAIProduct() &&
    !(product.hasVariants && product.variants?.length > 0) &&
    !(product.size && product.size.length > 0);

  // Preparar descripción para SEO (limpiar formato)
  const getSeoDescription = () => {
    if (!product.description) return `Comprá ${product.name} en ${SITE_NAME}. Envíos a todo Uruguay.`;
    const cleanDesc = product.description.replace(/\\n/g, ' ').replace(/\n/g, ' ').substring(0, 160);
    return cleanDesc + (product.description.length > 160 ? '...' : '');
  };

  const categoryName = product.category?.name || product.category;
  const categoryLanding = Object.values(CATEGORY_LANDINGS).find(
    (landing) => landing.categoryName === categoryName
  );
  const categoryUrl = categoryLanding?.path || '/tienda';
  const material = typeof product.material === 'string' ? product.material.trim() : '';
  const fit = typeof product.fit === 'string' ? product.fit.trim() : '';
  const careInstructions = typeof product.careInstructions === 'string'
    ? product.careInstructions.trim()
    : '';
  const relatedProducts = products
    .filter((item) => item._id !== product._id && item.category?.name === categoryName)
    .slice(0, 4);

  return (
    <>
      <SEO 
        title={product.name}
        description={getSeoDescription()}
        image={currentImage}
        url={`/product/${product._id}`}
        type="product"
        product={{
          name: product.name,
          description: product.description,
          price: parseFloat(discountedPrice),
          images: allImages,
          sku: product.sku || product._id,
          category: product.category,
          stock: product.stock,
          brand: SITE_NAME
        }}
        breadcrumbs={[
          { name: 'Inicio', url: '/' },
          { name: 'Tienda', url: '/tienda' },
          { name: categoryName || 'Productos', url: categoryUrl },
          { name: product.name, url: `/product/${product._id}` }
        ]}
      />
      <div className="single-product-container">
        {/* Breadcrumb */}
      <div className="container">
        <div className="product-breadcrumb-row">
          <button className="breadcrumb-back" onClick={() => navigate(-1)} title="Volver">
            <i className="fa-solid fa-arrow-left me-1"></i> Volver
          </button>
          <nav className="product-breadcrumb">
            <Link to="/" className="breadcrumb-item">Inicio</Link>
            <span className="breadcrumb-separator">/</span>
            <Link to="/tienda" className="breadcrumb-item">Tienda</Link>
            <span className="breadcrumb-separator">/</span>
            <Link to={categoryUrl} className="breadcrumb-item">
              {product.category?.name || product.category}
            </Link>
            <span className="breadcrumb-separator breadcrumb-hide-mobile">/</span>
            <span className="breadcrumb-current breadcrumb-hide-mobile">{product.name}</span>
          </nav>
        </div>
      </div>

      <div className="container">
        <div className="product-layout">
          {/* Product Gallery */}
          <div className="product-gallery">
            <div className="main-image-container">
              <div className="main-image-wrapper">
                <img 
                  src={currentImage} 
                  alt={`${product.name} ${currentImageIndex + 1}`}
                  className="main-product-image"
                />
                
                {/* Image Navigation */}
                {allImages.length > 1 && (
                  <>
                    <button 
                      className="image-nav-btn prev-btn"
                      onClick={prevImage}
                    >
                      <i className="fa-solid fa-chevron-left"></i>
                    </button>
                    <button 
                      className="image-nav-btn next-btn"
                      onClick={nextImage}
                    >
                      <i className="fa-solid fa-chevron-right"></i>
                    </button>
                    
                    {/* Image Indicators */}
                    <div className="image-indicators">
                      {allImages.map((_, index) => (
                        <button
                          key={index}
                          className={`indicator-dot ${index === currentImageIndex ? 'active' : ''}`}
                          onClick={() => goToImage(index)}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
            
            {/* Thumbnail Gallery */}
            {allImages.length > 1 && (
              <div className="thumbnail-gallery">
                {allImages.map((image, index) => (
                  <div
                    key={index}
                    className={`thumbnail-item ${index === currentImageIndex ? 'active' : ''}`}
                    onClick={() => goToImage(index)}
                  >
                    <img
                      src={image}
                      alt={`${product.name} ${index + 1}`}
                      className="thumbnail-image"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Product Details */}
          <div className="product-details">
            <div className="product-header">
              <h1 className="product-title">{product.name}</h1>
              
              {/* Product Meta */}
              <div className="product-meta">
                <div className="meta-item">
                  <i className="fa-solid fa-tag"></i>
                  <span>{product.category?.name || product.category}</span>
                </div>
                {product.subcategory && (
                  <div className="meta-item">
                    <i className="fa-solid fa-bookmark"></i>
                    <span>{product.subcategory}</span>
                  </div>
                )}
                {product.brand && (
                  <div className="meta-item">
                    <i className="fa-solid fa-award"></i>
                    <span>{product.brand.toLowerCase() === 'atelier' ? SITE_NAME : product.brand}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Price Section */}
            <div className="price-section">
              {product.discount > 0 ? (
                <div className="price-with-discount">
                  <div className="discount-badge">
                    <i className="fa-solid fa-percent me-1"></i>
                    {product.discount}% OFF
                  </div>
                  <div className="price-breakdown">
                    <span className="original-price">${currentPrice}</span>
                    <span className="final-price">${discountedPrice}</span>
                  </div>
                  <div className="savings">
                    <i className="fa-solid fa-piggy-bank me-1"></i>
                    Ahorras ${ahorro}
                  </div>
                </div>
              ) : (
                <div className="price-simple">
                  <span className="current-price">${currentPrice}</span>
                </div>
              )}
              {product.wholesalePrice > 0 && (
                <div className="mt-2 fw-semibold" style={{ fontSize: '0.85rem', color: '#000' }}>
                  Precio por mayor: ${product.wholesalePrice}
                </div>
              )}
            </div>

            {/* Product Description */}
            <div className="product-description">
              {product.description?.includes('<') ? (
                <HtmlFormattedDescription 
                  description={product.description} 
                  className="description-content"
                />
              ) : (
                <FormattedDescription 
                  description={product.description} 
                  className="description-content"
                />
              )}
            </div>

            {shouldShowStandaloneSizeGuide && (
              <div className="product-size-guide-banner">
                <button
                  type="button"
                  className="size-guide-link"
                  onClick={() => setShowSizeGuide(true)}
                >
                  <i className="fa-solid fa-ruler-combined me-1"></i>
                  {sizeGuideButtonLabel}
                </button>
                {product.sizeGuide?.note && (
                  <span className="product-size-guide-note">{product.sizeGuide.note}</span>
                )}
              </div>
            )}

            {/* AI Product Type Selection */}
            {isAIProduct() && (
              <div className="ai-product-selection">
                <div className="variant-group">
                  <label className="variant-label">
                    <i className="fa-solid fa-wand-magic-sparkles me-2"></i>
                    Selecciona el tipo de producto
                  </label>
                  <div className="ai-product-types">
                    {getAIProductTypes().map((type) => (
                      <button
                        key={type.id}
                        className={`ai-product-type-btn ${selectedAIProductType === type.id ? 'selected' : ''}`}
                        onClick={() => {
                          setSelectedAIProductType(type.id);
                          // Resetear talla y color si cambia el tipo
                          if (type.id !== 'remera') {
                            setSelectedAISize('');
                            setSelectedAIColor('');
                          }
                        }}
                      >
                        <i className={`${type.icon} type-icon`}></i>
                        <span className="type-name">{type.name}</span>
                        <span className="type-price">${type.price.toLocaleString('es-AR')}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Selectores de Talla y Color para Remeras AI */}
                {selectedAIProductType === 'remera' && (
                  <>
                    <div className="variant-group">
                      <div className="variant-label-row">
                        <label className="variant-label">
                          <i className="fa-solid fa-ruler me-2"></i>
                          Talla
                        </label>
                        {canShowSizeGuide && (
                          <button 
                            type="button"
                            className="size-guide-link"
                            onClick={() => setShowSizeGuide(true)}
                          >
                            <i className="fa-solid fa-ruler-combined me-1"></i>
                            {sizeGuideButtonLabel}
                          </button>
                        )}
                      </div>
                      <div className="variant-options">
                        {aiRemeraSizes.map((size) => (
                          <button
                            key={size}
                            className={`variant-btn size-btn ${selectedAISize === size ? 'selected' : ''}`}
                            onClick={() => setSelectedAISize(size)}
                          >
                            {size}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="variant-group">
                      <label className="variant-label">
                        <i className="fa-solid fa-palette me-2"></i>
                        Color
                      </label>
                      <div className="variant-options">
                        {aiRemeraColors.map((color) => (
                          <button
                            key={color}
                            className={`variant-btn color-btn ${selectedAIColor === color ? 'selected' : ''}`}
                            onClick={() => setSelectedAIColor(color)}
                          >
                            {color}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {selectedAIProductType && (
                  <div className="selected-variant-info">
                    <div className="variant-details">
                      <h6>
                        <i className="fa-solid fa-check-circle me-2"></i>
                        Producto Seleccionado
                      </h6>
                      <div className="variant-specs">
                        <span className="spec-item">
                          <strong>Tipo:</strong> {getAIProductTypes().find(t => t.id === selectedAIProductType)?.name}
                        </span>
                        {selectedAIProductType === 'remera' && selectedAISize && (
                          <span className="spec-item">
                            <strong>Talla:</strong> {selectedAISize}
                          </span>
                        )}
                        {selectedAIProductType === 'remera' && selectedAIColor && (
                          <span className="spec-item">
                            <strong>Color:</strong> {selectedAIColor}
                          </span>
                        )}
                        <span className="spec-item">
                          <strong>Precio:</strong> ${getAIProductTypes().find(t => t.id === selectedAIProductType)?.price.toLocaleString('es-AR')}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Variant Selection */}
            {!isAIProduct() && product.hasVariants && product.variants?.length > 0 ? (
              <div className="variant-selection">
                {/* Size Selection */}
                {getAvailableSizes().length > 0 && (
                  <div className="variant-group">
                    <div className="variant-label-row">
                      <label className="variant-label">
                        <i className="fa-solid fa-ruler me-2"></i>
                        Talla
                      </label>
                      {canShowSizeGuide && (
                        <button 
                          type="button"
                          className="size-guide-link"
                          onClick={() => setShowSizeGuide(true)}
                        >
                          <i className="fa-solid fa-ruler-combined me-1"></i>
                          {sizeGuideButtonLabel}
                        </button>
                      )}
                    </div>
                    <div className="variant-options">
                      {getAvailableSizes().map((size) => (
                        <button
                          key={size}
                          className={`variant-btn size-btn ${selectedSize === size ? 'selected' : ''}`}
                          onClick={() => handleSizeChange(size)}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Color Selection */}
                {getAvailableColors().length > 0 && (
                  <div className="variant-group">
                    <label className="variant-label">
                      <i className="fa-solid fa-palette me-2"></i>
                      Color
                    </label>
                    <div className="variant-options">
                      {getAvailableColors().map((color) => (
                        <button
                          key={color}
                          className={`variant-btn color-btn ${selectedColor === color ? 'selected' : ''}`}
                          onClick={() => handleColorChange(color)}
                          title={color}
                        >
                          {getColorHex(color) ? (
                            <span className="product-color-chip">
                              <span
                                className="product-color-swatch"
                                style={{ backgroundColor: getColorHex(color) }}
                              />
                              <span>{color}</span>
                            </span>
                          ) : (
                            color
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Selected Variant Info */}
                {selectedVariant && (
                  <div className="selected-variant-info">
                    <div className="variant-details">
                      <h6>
                        <i className="fa-solid fa-check-circle me-2"></i>
                        Variante Seleccionada
                      </h6>
                      <div className="variant-specs">
                        <span className="spec-item">
                          <strong>Talla:</strong> {selectedVariant.size}
                        </span>
                        <span className="spec-item">
                          <strong>Color:</strong> {selectedVariant.color}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="legacy-selection">
                {/* Legacy Size Selection */}
                {product.size && product.size.length > 0 && (
                  <div className="legacy-group">
                    <div className="variant-label-row">
                      <label className="legacy-label">
                        <i className="fa-solid fa-ruler me-2"></i>
                        Talles Disponibles
                      </label>
                      {canShowSizeGuide && (
                        <button
                          type="button"
                          className="size-guide-link"
                          onClick={() => setShowSizeGuide(true)}
                        >
                          <i className="fa-solid fa-ruler-combined me-1"></i>
                          {sizeGuideButtonLabel}
                        </button>
                      )}
                    </div>
                    <select
                      multiple
                      className="legacy-select"
                      value={selectedSizes}
                      onChange={handleSizeSelectChange}
                    >
                      {product.size.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Legacy Color Selection */}
                {product.color && product.color.length > 0 && (
                  <div className="legacy-group">
                    <label className="legacy-label">
                      <i className="fa-solid fa-palette me-2"></i>
                      Colores Disponibles
                    </label>
                    <select
                      multiple
                      className="legacy-select"
                      value={selectedColors}
                      onChange={handleColorSelectChange}
                    >
                      {product.color.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}

            {/* Quantity and Add to Cart */}
            <div className="purchase-section">
              <div className="quantity-section">
                <label className="quantity-label">
                  <i className="fa-solid fa-hashtag me-2"></i>
                  Cantidad
                </label>
                <div className="quantity-controls-single">
                  <button 
                    className="quantity-btn-single minus"
                    onClick={() => setQuantity(prev => Math.max(1, prev - 1))}
                    disabled={quantity <= 1}
                  >
                    <i className="fa-solid fa-minus"></i>
                  </button>
                  <span className="quantity-display-single">{quantity}</span>
                  <button 
                    className="quantity-btn-single plus"
                    onClick={() => setQuantity(prev => prev + 1)}
                  >
                    <i className="fa-solid fa-plus"></i>
                  </button>
                </div>
              </div>

              <div className="product-action-buttons">
                {/* Solo mostrar Agregar al Carrito si NO es personalizable */}
                {!isCustomizable() && (
                  <button 
                    onClick={handleAddToCart} 
                    className="add-to-cart-btn"
                    disabled={
                      (isAIProduct() && !selectedAIProductType) ||
                      (isAIProduct() && selectedAIProductType === 'remera' && (!selectedAISize || !selectedAIColor)) ||
                      (!isAIProduct() && product.hasVariants && (!selectedSize || !selectedColor))
                    }
                  >
                    <i className="fa-solid fa-cart-plus me-2"></i>
                    AGREGAR AL CARRITO
                  </button>
                )}
                
                {/* Virtual Try-On Button - No mostrar para productos personalizados */}
                {isTryOnEligible() && !isCustomizable() && (
                  <button 
                    onClick={() => setShowTryOnModal(true)}
                    className="try-on-btn"
                  >
                  <i class="fa-solid fa-wand-magic-sparkles me-2 "> </i> PROBARME
                  </button>
                )}

                {/* Customization Button for Personalizados */}
                {isCustomizable() && (
                  <button 
                    onClick={() => setShowCustomOrderForm(true)}
                    className="customize-btn"
                  >
                    <i className="fa-solid fa-pen-ruler me-2"></i>
                    SOLICITAR PERSONALIZACIÓN
                  </button>
                )}
              </div>

              {/* Premium Service Notice for Customizable Products */}
              {isCustomizable() && (
                <div className="premium-service-notice">
                  <div className="notice-icon">
                    <i className="fa-solid fa-gem"></i>
                  </div>
                  <div className="notice-content">
                    <span className="notice-title">Servicio Premium Personalizado</span>
                    <span className="notice-text">
                      Los productos personalizados requieren confirmación manual. 
                      Envía tu diseño y te contactaremos para confirmar disponibilidad y precio final según cantidad.
                    </span>
                  </div>
                </div>
              )}

              {/* AI Generator Link for Customizable Products */}
              {isCustomizable() && (
                <div className="ai-generator-banner">
                  <div className="ai-banner-content">
                    <i className="fa-solid fa-wand-magic-sparkles"></i>
                    <div className="ai-banner-text">
                      <span className="ai-banner-title">¿No tienes una imagen?</span>
                      <span className="ai-banner-subtitle">Crea diseños únicos con Inteligencia Artificial</span>
                    </div>
                    <span 
                      className="ai-banner-btn"
                      style={{ opacity: 0.5, cursor: 'default' }}
                    >
                      Próximamente
                      <i className="fa-solid fa-arrow-right ms-2"></i>
                    </span>
                  </div>
                </div>
              )}
              
              {/* Help Message - Para productos AI */}
              {isAIProduct() && !selectedAIProductType && (
                <div className="selection-help">
                  <i className="fa-solid fa-info-circle me-2"></i>
                  Selecciona el tipo de producto para continuar
                </div>
              )}
              
              {/* Help Message - Para remeras AI sin talla/color */}
              {isAIProduct() && selectedAIProductType === 'remera' && (!selectedAISize || !selectedAIColor) && (
                <div className="selection-help">
                  <i className="fa-solid fa-info-circle me-2"></i>
                  Selecciona talla y color para continuar
                </div>
              )}
              
              {/* Help Message - Solo para productos NO personalizables con variantes */}
              {!isAIProduct() && !isCustomizable() && product.hasVariants && (!selectedSize || !selectedColor) && (
                <div className="selection-help">
                  <i className="fa-solid fa-info-circle me-2"></i>
                  Selecciona talla y color para continuar
                </div>
              )}
            </div>

            {/* Additional Info */}
            {(material || fit || careInstructions || product.brand) && (
              <div className="additional-info">
                <h6 className="info-title">
                  <i className="fa-solid fa-info-circle me-2"></i>
                  Información Adicional
                </h6>
                <div className="info-grid">
                  {material && (
                    <div className="info-item">
                      <span className="info-label">Material</span>
                      <span className="info-value">{material}</span>
                    </div>
                  )}
                  {fit && (
                    <div className="info-item">
                      <span className="info-label">Calce</span>
                      <span className="info-value">{fit}</span>
                    </div>
                  )}
                  {careInstructions && (
                    <div className="info-item">
                      <span className="info-label">Cuidados</span>
                      <span className="info-value">{careInstructions}</span>
                    </div>
                  )}
                  {product.brand && (
                    <div className="info-item">
                      <span className="info-label">Marca</span>
                      <span className="info-value">{product.brand.toLowerCase() === 'atelier' ? SITE_NAME : product.brand}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {relatedProducts.length > 0 && (
        <section className="container related-products-section" aria-labelledby="related-products-title">
          <div className="section-header">
            <h2 id="related-products-title">También te puede interesar</h2>
          </div>
          <div className="products-grid store-products-grid">
            {relatedProducts.map((relatedProduct) => (
              <div key={relatedProduct._id} className="product-item-enhanced">
                <ProductCard product={relatedProduct} />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Virtual Try-On Modal */}
      <TryOnModal
        isOpen={showTryOnModal}
        onClose={() => setShowTryOnModal(false)}
        productImage={currentImage}
        productName={product.name}
      />

      {/* Custom Order Form Modal */}
      {showCustomOrderForm && (
        <div className="custom-order-modal-overlay" onClick={() => setShowCustomOrderForm(false)}>
          <div className="custom-order-modal" onClick={(e) => e.stopPropagation()}>
            <button 
              className="custom-order-close-btn"
              onClick={() => setShowCustomOrderForm(false)}
            >
              <i className="fa-solid fa-times"></i>
            </button>
            <CustomOrderForm 
              product={product}
              selectedVariant={selectedVariant}
              onClose={() => setShowCustomOrderForm(false)}
              initialQuantity={quantity}
            />
          </div>
        </div>
      )}

      {/* Size Guide Modal */}
      <SizeGuideModal 
        isOpen={showSizeGuide}
        onClose={() => setShowSizeGuide(false)}
        productType={fallbackSizeGuideType}
        customGuide={hasCustomSizeGuide ? product.sizeGuide : null}
      />
    </div>
    </>
  );
};

export default SingleProduct;
